/**
 * plugins/checklist-item.ts — Item de checklist (li em .checklist)
 *
 * Ferramentas: toggle de status (✓/✕/⏳), adicionar, mover ↑↓, excluir.
 * Prioridade: ANTES do list-item genérico pois .checklist > li é mais específico.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, directChildOf, moveSibling, closestInside } from '../types';

const CHECKLIST_STATUSES = [
    { value: 'yes',  icon: '✔',  cls: 'check-yes',  label: '✔ Feito' },
    { value: 'no',   icon: '✕',  cls: 'check-no',   label: '✕ Pendente' },
    { value: 'wait', icon: '⏳', cls: 'check-wait', label: '⏳ Aguardando' },
] as const;

export class ChecklistItemPlugin implements VtbPlugin {
    readonly selectorHints = ['.checklist > li', '[data-vtb-id="checklist"] > li'];
    readonly panelId = 'vtb-checklist';

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const container = closestInside(
            target, visualEditor,
            e => e.classList.contains('checklist') || e.dataset.vtbId === 'checklist',
        );
        if (!container) return null;
        const item = directChildOf(container, target);
        if (!item || item.tagName !== 'LI') return null;
        return { type: this.panelId, label: 'Item de Checklist', el: item, container };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        // Atualiza os botões de status para refletir o estado atual
        const span = ctx.el.querySelector<HTMLElement>('.check-yes, .check-no, .check-wait');
        const currentCls = span?.className ?? '';

        const btns = ['vtb-checklist-yes', 'vtb-checklist-no', 'vtb-checklist-wait'];
        btns.forEach(id => registry.q(id)?.classList.remove('vtb-btn-status-active'));

        for (const s of CHECKLIST_STATUSES) {
            if (currentCls.includes(s.cls)) {
                registry.q('vtb-checklist-' + s.value)?.classList.add('vtb-btn-status-active');
                break;
            }
        }
    }

    setup(registry: IVtbRegistry): void {
        for (const s of CHECKLIST_STATUSES) {
            const id = 'vtb-checklist-' + s.value;
            registry.q(id)?.addEventListener('click', () => this._setStatus(registry, s));
        }
        registry.q('vtb-checklist-add')?.addEventListener('click',  () => this._add(registry));
        registry.q('vtb-checklist-up')?.addEventListener('click',   () => this._move(registry, -1));
        registry.q('vtb-checklist-down')?.addEventListener('click', () => this._move(registry,  1));
        registry.q('vtb-checklist-del')?.addEventListener('click',  () => this._delete(registry));
    }

    // ── Operações ─────────────────────────────────────────────────────────────

    private _setStatus(registry: IVtbRegistry, s: typeof CHECKLIST_STATUSES[number]): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        // Troca o span de status
        const old = ctx.el.querySelector<HTMLElement>('.check-yes, .check-no, .check-wait');
        const newSpan = document.createElement('span');
        newSpan.className = s.cls;
        newSpan.textContent = s.icon;
        if (old) old.replaceWith(newSpan);
        else ctx.el.prepend(newSpan);

        // Atualiza o botão ativo
        (['vtb-checklist-yes', 'vtb-checklist-no', 'vtb-checklist-wait']).forEach(
            id => registry.q(id)?.classList.remove('vtb-btn-status-active'),
        );
        registry.q('vtb-checklist-' + s.value)?.classList.add('vtb-btn-status-active');

        registry.sync();
    }

    private _add(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const clone = ctx.el.cloneNode(true) as HTMLElement;
        clone.classList.remove('vtb-selected');
        // Garante status "pendente" no novo item
        const span = clone.querySelector<HTMLElement>('.check-yes, .check-no, .check-wait');
        if (span) { span.className = 'check-no'; span.textContent = '✕'; }
        // Muda texto para placeholder
        const text = clone.childNodes[clone.childNodes.length - 1];
        if (text?.nodeType === Node.TEXT_NODE) text.textContent = ' Novo item';
        if (ctx.el.nextSibling) ctx.el.parentElement!.insertBefore(clone, ctx.el.nextSibling);
        else ctx.el.parentElement!.appendChild(clone);
        registry.sync();
        registry.selectEl(clone);
    }

    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        moveSibling(ctx.el, dir);
        registry.sync();
    }

    private _delete(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        ctx.el.remove();
        registry.clearSelection();
        registry.sync();
    }
}
